// frontend/src/pages/dashboard/Taskhooks/useTaskRealtime.js

import { useEffect, useCallback } from "react";
import realtime, { pollingManager } from "../../../hooks/RealtimePoll";
import { toast } from "react-toastify";

export const useTaskRealtime = (authUser, hasPermission, expandedTaskId, setTasks, setTaskComments) => {
  // Task Realtime Updates
  useEffect(() => {
    realtime.start("tasks", {}, null, 10000); // 10 seconds
    return () => {
      realtime.stop("tasks");
    };
  }, []);

  const handleTaskUpdates = useCallback(
    (updatedTasks) => {
      if (!Array.isArray(updatedTasks) || updatedTasks.length === 0) return;
      const toastQueue = [];

      setTasks((prev) => {
        const existingMap = new Map(prev.map((p) => [Number(p.id), p]));
        const merged = [];

        updatedTasks.forEach((upd) => {
          const taskId = Number(upd.id);
          if (upd.is_deleted === true || upd.deleted === true) {
            if (existingMap.has(taskId)) {
              const deletedTask = existingMap.get(taskId);
              existingMap.delete(taskId);
              if (String(upd.created_by) !== String(authUser?.id))
                toastQueue.push({
                  type: "error",
                  message: `Task "${deletedTask?.title || upd.title}" was deleted`,
                });
            }
            return;
          }

          const isAssigned = (upd.assignedUsers || upd.assigned_users || []).some(
            (u) => Number(u.id) === Number(authUser?.id)
          );
          const isCreator = Number(upd.created_by) === Number(authUser?.id);

          if (!isAssigned && !isCreator && !hasPermission("task.view.all")) return;

          const enriched = {
            ...upd,
            assignedUsers: (upd.assignedUsers || upd.assigned_users || []).map(
              (u) => ({ ...u, role: u.role?.name || "No Role" })
            ),
            attachments: upd.attachments || [],
            statusObj: upd.status || null,
            status_id: upd.status_id,
            comment_count: upd.comment_count || 0,
            tracked_time: (() => {
              const tt = upd.tracked_time || {
                formatted: "00h 00m",
                is_running: false,
                total_seconds: 0,
              };
              const isPaused = !tt.is_running && (tt.total_seconds || 0) > 0;
              if (tt.end_time) return { ...tt, status: "Ended" };
              else if (tt.is_running) return { ...tt, status: "Running" };
              else if (isPaused) return { ...tt, status: "Paused" };
              else return { ...tt, status: "NotStarted" };
            })(),
          };

          if (existingMap.has(taskId)) {
            merged.push({ ...existingMap.get(taskId), ...enriched });
            existingMap.delete(taskId);
          } else {
            merged.push(enriched);
            if (String(upd.created_by) !== String(authUser?.id))
              toastQueue.push({
                type: "success",
                message: `🆕 Task "${upd.title}" created`,
              });
          }

          if (taskId === expandedTaskId) {
            clearTimeout(window.__reloadTimer);
            window.__reloadTimer = setTimeout(() => {
              // Reload comments for expanded task
            }, 50);
          }
        });

        existingMap.forEach((p) => merged.push(p));
        return merged.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
      });

      toastQueue.forEach(({ type, message }) => {
        if (type === "success") toast.success(message);
        else if (type === "error") toast.error(message);
      });
    },
    [authUser?.id, expandedTaskId, hasPermission, setTasks]
  );

  useEffect(() => {
    realtime.on("tasks", handleTaskUpdates);
    return () => realtime.off("tasks", handleTaskUpdates);
  }, [handleTaskUpdates]);

  // Comments Realtime
  useEffect(() => {
    if (!expandedTaskId) return;
    const key = `comments-task-${expandedTaskId}`;

    const handleNewComments = (incoming) => {
      if (!incoming || incoming.length === 0) return;
      const deletes = incoming.filter((c) => c.is_deleted);
      const newOnes = incoming.filter((c) => !c.is_deleted);

      if (deletes.length > 0) {
        setTaskComments((prev) => {
          const updated = { ...prev };
          deletes.forEach((d) => {
            Object.keys(updated).forEach((tid) => {
              updated[tid] = (updated[tid] || []).filter((c) => c.id !== d.id);
            });
          });
          return updated;
        });
      }

      if (newOnes.length > 0) {
        setTaskComments((prev) => {
          const updated = { ...prev };
          newOnes.forEach((c) => {
            const tid = c.task_id;
            const list = updated[tid] || [];
            const exists = list.some((x) => x.id === c.id);
            if (!exists)
              updated[tid] = [c, ...list].sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
              );
          });
          return updated;
        });

        newOnes.forEach((c) => {
          if (c.user_id !== authUser?.id && c.task_id === expandedTaskId)
            toast.info(
              `💬 ${c.user?.name}: ${c.content?.slice(0, 100) || "New comment"}`
            );
        });
      }
    };

    realtime.on(key, handleNewComments);
    realtime.start("comments", { task_id: expandedTaskId });
    return () => {
      realtime.off(key, handleNewComments);
      realtime.stop(key);
    };
  }, [expandedTaskId, authUser, setTaskComments]);

  return { handleTaskUpdates };
};